import { useState } from "react";
import axios from "axios";
import { Heart, Star } from "lucide-react";
import Seo from "@/components/Seo";
import { FadeUp, Overline, GoldRule } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SERVICES = ["Meal prep", "Pop-up or event", "Catering"];

function Rating({ value, onChange, icon: Icon, active, testidPrefix, label }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex gap-1.5" role="radiogroup" aria-label={label}>
      {[1, 2, 3, 4, 5].map((n) => {
        const filled = (hover || value) >= n;
        return (
          <button
            key={n}
            type="button"
            role="radio"
            aria-checked={value === n}
            aria-label={`${n} of 5`}
            data-testid={`${testidPrefix}-${n}`}
            onMouseEnter={() => setHover(n)}
            onMouseLeave={() => setHover(0)}
            onClick={() => onChange(n)}
            className="cursor-pointer p-0.5 transition-transform duration-150 hover:scale-110"
          >
            <Icon size={28} strokeWidth={1.75} className={filled ? active : "text-forest/25"} fill={filled ? "currentColor" : "none"} />
          </button>
        );
      })}
    </div>
  );
}

const EMPTY = {
  name: "",
  service_type: "",
  heart_rating: 0,
  favorite_dish: "",
  suggestions: "",
  reorder_rating: 0,
  share_consent: null,
  email: "",
};

export default function Feedback() {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!form.service_type) return setError("Pick what you had.");
    if (!form.heart_rating) return setError("Tap the hearts — how was it overall?");
    if (form.share_consent === null) return setError("Let us know if we can share your words.");
    setBusy(true);
    setError("");
    try {
      await axios.post(`${API}/reviews`, {
        name: form.name || null,
        service_type: form.service_type,
        heart_rating: form.heart_rating,
        favorite_dish: form.favorite_dish || null,
        suggestions: form.suggestions || null,
        reorder_rating: form.reorder_rating || null,
        share_consent: form.share_consent,
        email: form.email || null,
      });
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <Seo
        title="Feedback — How was it? · Sita"
        description="Tell Sita how your meal prep, pop-up, or catered event was — a minute of your time helps us make the next table even better."
      />

      <section data-testid="feedback-hero" className="mx-auto max-w-7xl px-6 pb-10 pt-14 md:px-10 md:pb-14 md:pt-20">
        <div className="max-w-3xl">
          <FadeUp>
            <Overline>Feedback &amp; reviews</Overline>
            <h1 className="mt-4 font-display text-5xl font-light leading-[1.05] text-forest md:text-6xl">How was it?</h1>
            <GoldRule className="mt-8" />
            <p className="mt-8 text-lg leading-relaxed text-ink/85">
              A minute of your time helps us make the next table even better.
            </p>
          </FadeUp>
        </div>
      </section>

      <section data-testid="feedback-form-section" className="mx-auto max-w-3xl px-6 pb-24 md:pb-32">
        {done ? (
          <FadeUp>
            <div data-testid="feedback-success-panel" className="rounded-3xl border border-forest/15 bg-white/50 px-8 py-16 text-center">
              <span className="mx-auto block h-px w-14 bg-gold" aria-hidden="true" />
              <h2 className="mt-6 font-display text-3xl font-light text-forest">Thank you. This means the world to us.</h2>
              <p className="mt-4 text-ink/70">Every word goes straight to the kitchen.</p>
            </div>
          </FadeUp>
        ) : (
          <FadeUp delay={0.1}>
            <form onSubmit={submit} data-testid="feedback-form" className="space-y-9 rounded-3xl border border-forest/15 bg-white/50 p-6 md:p-10">
              <div>
                <label className="field-label" htmlFor="fb-name">Your name (optional)</label>
                <input id="fb-name" value={form.name} onChange={set("name")} data-testid="feedback-name-input" className="field" placeholder="First name is plenty" />
              </div>

              <div>
                <span className="field-label">What did you have? *</span>
                <div className="flex flex-wrap gap-3">
                  {SERVICES.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, service_type: s }))}
                      data-testid={`feedback-service-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      aria-pressed={form.service_type === s}
                      className={`cursor-pointer rounded-full border px-5 py-2.5 text-sm font-medium transition-[background-color,color,border-color] duration-300 ${
                        form.service_type === s
                          ? "border-terracotta bg-terracotta text-cream"
                          : "border-forest/25 text-forest hover:border-terracotta/60"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <span className="field-label">How was it overall? *</span>
                <Rating
                  value={form.heart_rating}
                  onChange={(n) => setForm((f) => ({ ...f, heart_rating: n }))}
                  icon={Heart}
                  active="text-terracotta"
                  testidPrefix="feedback-heart"
                  label="Overall rating out of 5"
                />
              </div>

              <div>
                <label className="field-label" htmlFor="fb-fav">What was your favorite dish?</label>
                <input id="fb-fav" value={form.favorite_dish} onChange={set("favorite_dish")} data-testid="feedback-favorite-input" className="field" placeholder="The one you're still thinking about" />
              </div>

              <div>
                <label className="field-label" htmlFor="fb-suggest">Anything we should make again, or change?</label>
                <textarea id="fb-suggest" rows="4" value={form.suggestions} onChange={set("suggestions")} data-testid="feedback-suggestions-textarea" className="field resize-y" placeholder="We read every word." />
              </div>

              <div>
                <span className="field-label">How likely are you to order again?</span>
                <Rating
                  value={form.reorder_rating}
                  onChange={(n) => setForm((f) => ({ ...f, reorder_rating: n }))}
                  icon={Star}
                  active="text-gold"
                  testidPrefix="feedback-star"
                  label="Likelihood of ordering again out of 5"
                />
              </div>

              <div>
                <span className="field-label">Can we share your words on our page? *</span>
                <div className="flex gap-3">
                  {[true, false].map((v) => (
                    <button
                      key={String(v)}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, share_consent: v }))}
                      data-testid={`feedback-share-${v ? "yes" : "no"}`}
                      aria-pressed={form.share_consent === v}
                      className={`cursor-pointer rounded-full border px-6 py-2.5 text-sm font-medium transition-[background-color,color,border-color] duration-300 ${
                        form.share_consent === v
                          ? "border-terracotta bg-terracotta text-cream"
                          : "border-forest/25 text-forest hover:border-terracotta/60"
                      }`}
                    >
                      {v ? "Yes" : "No"}
                    </button>
                  ))}
                </div>
                <p className="mt-2 text-xs text-ink/50">Only words you say yes to ever appear publicly.</p>
              </div>

              <div>
                <label className="field-label" htmlFor="fb-email">Email for new menus (optional)</label>
                <input id="fb-email" type="email" value={form.email} onChange={set("email")} data-testid="feedback-email-input" className="field" placeholder="You'll join the email list" />
              </div>

              {error && (
                <p role="alert" data-testid="feedback-error-alert" className="rounded-lg bg-terracotta/10 px-4 py-3 text-sm text-terracotta">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={busy}
                data-testid="feedback-submit-button"
                className="inline-flex cursor-pointer items-center justify-center rounded-full bg-terracotta px-10 py-3.5 text-sm font-medium tracking-wide text-cream transition-[background-color,color,transform] duration-300 hover:-translate-y-0.5 hover:bg-gold hover:text-deepforest disabled:opacity-60"
              >
                {busy ? "Sending…" : "Send feedback"}
              </button>
            </form>
          </FadeUp>
        )}
      </section>
    </div>
  );
}
